import * as React from 'react';

import { connect, useDispatch } from 'react-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { getFullName } from '@webapp/store/user/selectors';

// Types
import User from '@common/types/user/user';

// Transalte
import { Translate } from 'react-localize-redux';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';

// Components
import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Typography from '@components/basics/typography';
import FilterIcon from '@components/basics/icons/filter';

import ReleasesCatalogMenuContent from './releases-menu-content';
import MerchCatalogMenuContent from './merch-menu-content';

import { CatalogTabs } from '@webapp/pages/catalog';
import NFTCatalogMenuContent from './nft-menu-content';
import CatalogFilter from '@common/types/catalog/catalog-filter-type';
import Button from '@components/actions/button';
import { CloseMenu } from '@webapp/store/ui/action-creators';

interface CatalogMenuProps extends Partial<User> {
  className?: string;
  isMobile?: boolean;
  hasChanges?: boolean;
  section: CatalogTabs;
  filterOption: CatalogFilter;
  setFilterOption: React.Dispatch<React.SetStateAction<CatalogFilter>>;
}

const mapStateToProps = (state: AppState) => ({
  avatar: state.user.profile?.avatar,
  fullName: getFullName(state),
  isMobile: state.ui.mobile,
});

export const CatalogMenu: React.FC<CatalogMenuProps> = ({
  className,
  avatar,
  fullName,
  section,
  filterOption,
  setFilterOption,
  hasChanges,
  isMobile,
}) => {
  const dispatch = useDispatch();

  const [filter, setFilter] = React.useState<CatalogFilter>(filterOption);

  const applyFilter = () => {
    setFilterOption(filter);
    dispatch(new CloseMenu());
  };

  const getContent = (section: CatalogTabs) => {
    switch (section) {
      case 'releases':
        return (
          <ReleasesCatalogMenuContent
            filterOption={filter}
            setFilterOption={setFilter}
          />
        );
      case 'merch':
        return (
          <MerchCatalogMenuContent
            filterOption={filter}
            setFilterOption={setFilter}
          />
        );
      case 'nft':
        return <NFTCatalogMenuContent />;

      default:
        break;
    }
  };

  return (
    <NewMenu
      className={className}
      button={
        <PillButton
          className={hasChanges ? 'has-changes' : ''}
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          <Typography className="menu-btn-title" weight="700">
            <Translate id="ANALYTICS.MENU.BUTTON.LABEL" />
          </Typography>
          <FilterIcon invertedTheme={hasChanges} />
        </PillButton>
      }
      menuId="CATALOG_MENU"
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      footer={
        <Button fullWidth onClick={applyFilter}>
          <Translate id="CATALOG.MENU.BUTTON.SAVE" />
        </Button>
      }
    >
      {getContent(section)}
    </NewMenu>
  );
};

export default connect(mapStateToProps)(CatalogMenu);

const PillButton = styled(Flex)`
  cursor: pointer;
  padding: 12px 18px;
  border-radius: 100px;
  opacity: 1;
  background-color: ${({ theme }) => theme.colors.midGray};
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.75;
  }

  &.has-changes {
    background-color: ${({ theme }) => theme.colors.primary};

    .menu-btn-title {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }
`;
